// 管理后台接口封装
// 注意：Secret 不再持久化，只存在当前 AdminDashboard 组件生命周期的内存中
import { authenticatedFetch } from '../utils/api';
import { API_HOST } from '../utils/constants';

// 内存中的 Secret（离开页面自动丢失）
let currentSecret = '';

export const getAdminSecret = () => currentSecret;

export const setAdminSecret = (s) => {
  currentSecret = s || '';
};

export const clearAdminSecret = () => {
  currentSecret = '';
};

const adminFetch = (path, options = {}) => {
  const method = String(options.method || 'GET').toUpperCase();
  const timeout = options.timeout ?? (method === 'GET' ? 45_000 : undefined);
  return authenticatedFetch(`${API_HOST}${path}`, {
    ...options,
    ...(timeout ? { timeout } : {}),
    headers: {
      ...(options.headers || {}),
      'X-Admin-Secret': currentSecret,
    },
  });
};

export const fetchLiveTasks = (filters = {}, requestOptions = {}) => {
  const qs = new URLSearchParams();
  ['projectId', 'userId'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  return adminFetch(`/api/admin/realtime/tasks${qs.toString() ? `?${qs}` : ''}`, requestOptions);
};

export const fetchOverview = (startTime, endTime, projectId, userId) => {
  const qs = new URLSearchParams();
  if (startTime != null) qs.set('startTime', startTime);
  if (endTime != null) qs.set('endTime', endTime);
  if (projectId != null && projectId !== '') qs.set('projectId', projectId);
  if (userId != null && userId !== '') qs.set('userId', userId);
  return adminFetch(`/api/admin/overview${qs.toString() ? `?${qs}` : ''}`);
};

export const fetchUserStats = (startTime, endTime, projectId, userId) => {
  const qs = new URLSearchParams();
  if (startTime != null) qs.set('startTime', startTime);
  if (endTime != null) qs.set('endTime', endTime);
  if (projectId != null && projectId !== '') qs.set('projectId', projectId);
  if (userId != null && userId !== '') qs.set('userId', userId);
  return adminFetch(`/api/admin/overview/users${qs.toString() ? `?${qs}` : ''}`);
};

export const fetchUserDetail = (userId, startTime, endTime, projectId) => {
  const qs = new URLSearchParams();
  if (startTime != null) qs.set('startTime', startTime);
  if (endTime != null) qs.set('endTime', endTime);
  if (projectId != null && projectId !== '') qs.set('projectId', projectId);
  return adminFetch(`/api/admin/overview/users/${encodeURIComponent(userId)}${qs.toString() ? `?${qs}` : ''}`);
};

export const fetchProjects = (filters = {}) => {
  const qs = new URLSearchParams();
  ['keyword', 'userId', 'status', 'limit'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  if (!qs.has('limit')) qs.set('limit', '500');
  return adminFetch(`/api/admin/projects?${qs}`);
};

export const fetchProjectFullTrace = (projectId) => adminFetch(`/api/admin/projects/${encodeURIComponent(projectId)}/full`);

export const fetchMiniStoryboardStatistics = (filters = {}, options = {}) => {
  const qs = new URLSearchParams({ page: String(filters.page ?? 0), size: String(filters.size ?? 20) });
  ['projectId', 'keyword'].forEach(key => {
    if (filters[key] != null && filters[key] !== '') qs.set(key, filters[key]);
  });
  return adminFetch(`/api/admin/mini-storyboard-statistics/episodes?${qs}`, options);
};

export const fetchMiniStoryboardSegmentStatistics = (projectId, episodeId, options = {}) => (
  adminFetch(`/api/admin/mini-storyboard-statistics/episodes/${encodeURIComponent(episodeId)}/segments?${new URLSearchParams({ projectId })}`, options)
);

export const updateAdminProjectStatus = (projectId, status) => adminFetch(`/api/admin/projects/${encodeURIComponent(projectId)}/status`, {
  method: 'PATCH',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ status }),
});

export const updateAdminProjectOwner = (projectId, userId) => adminFetch(`/api/admin/projects/${encodeURIComponent(projectId)}/owner`, {
  method: 'PATCH',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ userId }),
});

export const archiveAdminProject = (projectId) => adminFetch(`/api/admin/projects/${encodeURIComponent(projectId)}/archive`, {
  method: 'POST',
});

export const restoreAdminProject = (projectId, status = 'production') => adminFetch(`/api/admin/projects/${encodeURIComponent(projectId)}/restore`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ status }),
});

export const fetchRecycleBin = (filters = {}, requestOptions = {}) => {
  const query = new URLSearchParams({ page: String(filters.page ?? 0), size: String(filters.size ?? 20) });
  ['type', 'keyword'].forEach(key => {
    if (filters[key]) query.set(key, filters[key]);
  });
  return adminFetch(`/api/admin/recycle-bin?${query}`, requestOptions);
};

export const restoreRecycleBinEntry = (entryId) => adminFetch(`/api/admin/recycle-bin/${encodeURIComponent(entryId)}/restore`, {
  method: 'POST',
});

export const permanentlyDeleteRecycleBinEntry = (entryId, confirmId) => adminFetch(`/api/admin/recycle-bin/${encodeURIComponent(entryId)}?${new URLSearchParams({ confirmId: String(confirmId) })}`, {
  method: 'DELETE',
});

export const fetchAdminUsers = (filters = {}) => {
  const qs = new URLSearchParams();
  ['keyword', 'status', 'limit'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  if (!qs.has('limit')) qs.set('limit', '500');
  return adminFetch(`/api/admin/users-v2?${qs}`);
};

export const createAdminUser = (body) => adminFetch('/api/admin/users-v2', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body || {}),
});

export const resetAdminUserPassword = (userId, body) => adminFetch(`/api/admin/users-v2/${encodeURIComponent(userId)}/reset-password`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body || {}),
});

export const updateAdminUserStatus = (userId, status) => adminFetch(`/api/admin/users-v2/${encodeURIComponent(userId)}/status`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ status }),
});

export const updateAdminUserRole = (userId, role) => adminFetch(`/api/admin/users-v2/${encodeURIComponent(userId)}/role`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ role }),
});

export const revokeAdminUserSessions = (userId) => adminFetch(`/api/admin/users-v2/${encodeURIComponent(userId)}/revoke-sessions`, {
  method: 'POST',
});

export const fetchAdminUserSessions = (userId) => adminFetch(`/api/admin/users-v2/${encodeURIComponent(userId)}/sessions`);

/**
 * 单个账号的服务端聚合用量。这里仅发送日期范围，前端不会下载任务/积分流水自行统计。
 * appUserId 是 app_user.id（数字），不是业务用户名。
 */
export const fetchAdminUserUsageStatistics = (appUserId, range = {}) => {
  const qs = new URLSearchParams();
  ['from', 'to', 'days'].forEach((key) => {
    if (range[key] != null && range[key] !== '') qs.set(key, range[key]);
  });
  const query = qs.toString();
  return adminFetch(`/api/admin/users-v2/${encodeURIComponent(appUserId)}/usage-statistics${query ? `?${query}` : ''}`);
};

export const fetchErrors = (filters = {}) => {
  const qs = new URLSearchParams();
  ['projectId', 'userId', 'source', 'limit'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  if (!qs.has('limit')) qs.set('limit', '100');
  return adminFetch(`/api/admin/errors?${qs}`);
};

export const fetchErrorGroups = (filters = {}) => {
  const qs = new URLSearchParams();
  ['projectId', 'userId', 'source', 'limit'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  if (!qs.has('limit')) qs.set('limit', '100');
  return adminFetch(`/api/admin/errors/groups?${qs}`);
};

export const fetchCosts = (filters = {}) => {
  const qs = new URLSearchParams();
  ['projectId', 'userId', 'type', 'startTime', 'endTime', 'limit'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  if (!qs.has('limit')) qs.set('limit', '300');
  return adminFetch(`/api/admin/costs?${qs}`);
};

const fetchCostAggregation = (path, filters = {}) => {
  const qs = new URLSearchParams();
  ['projectId', 'userId', 'type', 'startTime', 'endTime', 'limit', 'bucket'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  return adminFetch(`/api/admin/costs/${path}${qs.toString() ? `?${qs}` : ''}`);
};

export const fetchCostTrend = (filters = {}) => fetchCostAggregation('trend', filters);
export const fetchCostsByUser = (filters = {}) => fetchCostAggregation('by-user', filters);
export const fetchCostsByProject = (filters = {}) => fetchCostAggregation('by-project', filters);
export const fetchCostsByModel = (filters = {}) => fetchCostAggregation('by-model', filters);

export const backfillMissingCosts = (limit = 1000, dryRun = false) => {
  const qs = new URLSearchParams();
  qs.set('limit', limit);
  qs.set('dryRun', dryRun ? 'true' : 'false');
  return adminFetch(`/api/admin/costs/backfill?${qs}`, { method: 'POST' });
};

export const recomputeTaskCosts = (filters = {}) => {
  const qs = new URLSearchParams();
  ['projectId', 'userId', 'provider', 'model', 'startTime', 'endTime'].forEach((key) => {
    if (filters[key] != null && filters[key] !== '') qs.set(key, filters[key]);
  });
  qs.set('onlyZeroOrNull', filters.onlyZeroOrNull === false ? 'false' : 'true');
  return adminFetch(`/api/admin/costs/recompute?${qs}`, { method: 'POST' });
};

export const fetchPointAccounts = (filters = {}) => {
  const qs = new URLSearchParams();
  ['keyword', 'limit'].forEach((key) => {
    if (filters[key] != null && filters[key] !== '') qs.set(key, filters[key]);
  });
  qs.set('onlyWarning', filters.onlyWarning ? 'true' : 'false');
  return adminFetch(`/api/admin/points/accounts?${qs}`);
};

export const fetchPointUserDetail = (userId, ledgerLimit = 200) => {
  const qs = new URLSearchParams({ ledgerLimit: String(ledgerLimit) });
  return adminFetch(`/api/admin/points/users/${encodeURIComponent(userId)}?${qs}`);
};

export const grantUserPoints = (userId, body = {}) => adminFetch(`/api/admin/points/users/${encodeURIComponent(userId)}/grant`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const adjustUserPoints = (userId, body = {}) => adminFetch(`/api/admin/points/users/${encodeURIComponent(userId)}/adjust`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const updateUserPointWarningThreshold = (userId, warningThreshold) => adminFetch(`/api/admin/points/users/${encodeURIComponent(userId)}/warning-threshold`, {
  method: 'PATCH',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ warningThreshold }),
});

export const updateUserPointDailyWarningThreshold = (userId, dailyWarningThreshold) => adminFetch(`/api/admin/points/users/${encodeURIComponent(userId)}/daily-warning-threshold`, {
  method: 'PATCH',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ dailyWarningThreshold }),
});

export const createUserPointPlan = (userId, body = {}) => adminFetch(`/api/admin/points/users/${encodeURIComponent(userId)}/plans`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const updateUserPointPlan = (planId, body = {}) => adminFetch(`/api/admin/points/plans/${encodeURIComponent(planId)}`, {
  method: 'PATCH',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const deleteUserPointPlan = (planId) => adminFetch(`/api/admin/points/plans/${encodeURIComponent(planId)}`, {
  method: 'DELETE',
});

// ============ 积分中心（双轨统计 / 明细 / 核算 / 限额策略） ============

export const fetchPointStats = (filters = {}) => {
  const qs = new URLSearchParams();
  ['from', 'to', 'granularity', 'dimension', 'userId', 'projectId'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  return adminFetch(`/api/admin/points/stats${qs.toString() ? `?${qs}` : ''}`);
};

export const fetchPointLedger = (filters = {}) => {
  const qs = new URLSearchParams();
  ['userId', 'projectId', 'from', 'to', 'page', 'size'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  return adminFetch(`/api/admin/points/ledger${qs.toString() ? `?${qs}` : ''}`);
};

export const fetchPointProjectSettlement = (filters = {}) => {
  const qs = new URLSearchParams();
  ['projectId', 'from', 'to'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  return adminFetch(`/api/admin/points/project-settlement${qs.toString() ? `?${qs}` : ''}`);
};

export const fetchPointPolicies = () => adminFetch('/api/admin/points/policies');

export const createPointPolicy = (body = {}) => adminFetch('/api/admin/points/policies', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const updatePointPolicy = (policyId, body = {}) => adminFetch(`/api/admin/points/policies/${encodeURIComponent(policyId)}`, {
  method: 'PATCH',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const fetchActivity = (filters = {}, page = 0, size = 50) => {
  const qs = new URLSearchParams();
  ['userId', 'action', 'projectId', 'status', 'startTime', 'endTime'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  qs.set('page', page);
  qs.set('size', size);
  return adminFetch(`/api/admin/activity?${qs}`);
};

export const fetchAiTaskOverview = (filters = {}, requestOptions = {}) => {
  const qs = new URLSearchParams();
  ['projectId', 'userId', 'type', 'status', 'source'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  return adminFetch(`/api/admin/ai-tasks/overview${qs.toString() ? `?${qs}` : ''}`, requestOptions);
};

export const fetchAiTasks = (filters = {}, requestOptions = {}) => {
  const qs = new URLSearchParams();
  ['projectId', 'userId', 'type', 'status', 'source', 'limit'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  if (!qs.has('limit')) qs.set('limit', '200');
  return adminFetch(`/api/admin/ai-tasks?${qs}`, requestOptions);
};

export const fetchAiTaskDetail = (internalTaskId) => adminFetch(`/api/admin/ai-tasks/${encodeURIComponent(internalTaskId)}`);

export const fetchAiTaskFullTrace = (internalTaskId) => adminFetch(`/api/admin/ai-tasks/${encodeURIComponent(internalTaskId)}/full`);

export const fetchPromptCatalog = (filters = {}) => {
  const qs = new URLSearchParams();
  ['q', 'category', 'sourceType', 'modality', 'featureLocation', 'modelScope', 'language', 'market', 'enabledKey'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  return adminFetch(`/api/admin/prompts${qs.toString() ? `?${qs}` : ''}`);
};

export const createPromptTemplate = (body = {}) => adminFetch('/api/admin/prompts', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const updatePromptTemplate = (id, body = {}) => adminFetch(`/api/admin/prompts/${encodeURIComponent(id)}`, {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const resetPromptTemplate = (id) => adminFetch(`/api/admin/prompts/${encodeURIComponent(id)}/reset`, {
  method: 'POST',
});

export const deletePromptTemplate = (id) => adminFetch(`/api/admin/prompts/${encodeURIComponent(id)}`, {
  method: 'DELETE',
});

export const fetchVisualReferences = (filters = {}, requestOptions = {}) => {
  const qs = new URLSearchParams();
  ['q', 'category', 'status', 'mediaType', 'market'].forEach((k) => {
    if (filters[k] != null && filters[k] !== '') qs.set(k, filters[k]);
  });
  return adminFetch(`/api/admin/visual-references${qs.toString() ? `?${qs}` : ''}`, requestOptions);
};

export const createVisualReference = (body = {}) => adminFetch('/api/admin/visual-references', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const updateVisualReference = (id, body = {}) => adminFetch(`/api/admin/visual-references/${encodeURIComponent(id)}`, {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const deleteVisualReference = (id) => adminFetch(`/api/admin/visual-references/${encodeURIComponent(id)}`, {
  method: 'DELETE',
});

export const generateVisualReference = (id, body = {}) => adminFetch(`/api/admin/visual-references/${encodeURIComponent(id)}/generate`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const syncVisualReference = (id) => adminFetch(`/api/admin/visual-references/${encodeURIComponent(id)}/sync`, {
  method: 'POST',
});

export const seedDefaultVisualReferences = () => adminFetch('/api/admin/visual-references/seed-defaults', {
  method: 'POST',
});

export const updateAiTaskErrorStatus = (internalTaskId, status, note = '') => adminFetch(`/api/admin/ai-tasks/${encodeURIComponent(internalTaskId)}/error-status`, {
  method: 'PATCH',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ status, note }),
});

export const retryAiTask = (internalTaskId) => adminFetch(`/api/admin/ai-tasks/${encodeURIComponent(internalTaskId)}/retry`, {
  method: 'POST',
});

export const cancelAiTask = (internalTaskId) => adminFetch(`/api/admin/ai-tasks/${encodeURIComponent(internalTaskId)}/cancel`, {
  method: 'POST',
});

export const requeueStaleAiTasks = (minutes = 360) => adminFetch(`/api/admin/ai-tasks/requeue-stale?minutes=${encodeURIComponent(minutes)}`, {
  method: 'POST',
});

export const fetchGenerationMaintenance = (requestOptions = {}) => adminFetch('/api/admin/generation-maintenance', requestOptions);

export const updateGenerationMaintenance = (blocked) => adminFetch('/api/admin/generation-maintenance', {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ blocked: Boolean(blocked) }),
});

export const fetchRuntimeSecrets = () => adminFetch('/api/admin/runtime-secrets');

export const fetchHongguoConfig = (requestOptions = {}) => adminFetch('/api/admin/hongguo/config', requestOptions);

// A paid run is only started by an explicit UI action; never retry this POST automatically.
export const startHongguoRun = (body, requestOptions = {}) => adminFetch('/api/admin/hongguo/runs', {
  ...requestOptions,
  timeout: 120_000,
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const fetchHongguoRun = (runId, requestOptions = {}) => adminFetch(`/api/admin/hongguo/runs/${encodeURIComponent(runId)}`, requestOptions);

// Rankings are independent of Apify and project filters. GET only reads saved database rows.
export const fetchHongguoRankings = (board, page = 1, requestOptions = {}) => {
  const query = new URLSearchParams({ board, page: String(page) });
  return adminFetch(`/api/admin/hongguo/rankings?${query}`, requestOptions);
};

// One explicit click syncs all five pages of this board and returns the requested saved page.
// Never automatically replay this POST; a GET can verify an uncertain outcome without crawling.
export const refreshHongguoRankings = (board, page = 1, requestOptions = {}) => {
  const query = new URLSearchParams({ board, page: String(page) });
  return adminFetch(`/api/admin/hongguo/rankings/refresh?${query}`, {
    ...requestOptions,
    timeout: 60_000,
    method: 'POST',
  });
};

export const verifyRuntimeSecretPassphrase = (passphrase) => adminFetch('/api/admin/runtime-secrets/verify', {
  method: 'POST',
  headers: { 'X-Config-Passphrase': passphrase || '' },
});

export const updateRuntimeSecret = (key, value, passphrase) => adminFetch(`/api/admin/runtime-secrets/${encodeURIComponent(key)}`, {
  method: 'PUT',
  headers: {
    'Content-Type': 'application/json',
    'X-Config-Passphrase': passphrase || '',
  },
  body: JSON.stringify({ value }),
});

export const resetRuntimeSecret = (key, passphrase) => adminFetch(`/api/admin/runtime-secrets/${encodeURIComponent(key)}`, {
  method: 'DELETE',
  headers: { 'X-Config-Passphrase': passphrase || '' },
});

/**
 * 用提供的 secret 试一次最小请求，验证是否有效。
 * 成功则写入内存 secret 并 resolve；失败 reject。
 */
export const verifyAdminSecret = async (secret) => {
  try {
    const res = await authenticatedFetch(`${API_HOST}/api/admin/realtime/tasks`, {
      headers: { 'X-Admin-Secret': secret },
      timeout: 45_000,
    });
    setAdminSecret(secret);
    return res;
  } catch (e) {
    clearAdminSecret();
    throw e;
  }
};

// A single explicit analysis intent. Never automatically replay this paid POST.
export const startHongguoRankingAnalysis = ({ board, page, seriesId, requestId }, requestOptions = {}) => adminFetch('/api/admin/hongguo/rankings/analysis', {
  ...requestOptions,
  timeout: 45_000,
  method: 'POST',
  headers: { 'Content-Type': 'application/json', 'X-Ai-Provider': 'huobao_gpt' },
  body: JSON.stringify({ board, page, seriesId, requestId }),
});

export const fetchHongguoRankingAnalysis = (requestId, requestOptions = {}) => adminFetch(`/api/admin/hongguo/rankings/analysis/${encodeURIComponent(requestId)}`, {
  ...requestOptions,
  timeout: 15_000,
});
